import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

// MSAL v2 Imports
import { MsalGuard } from '@azure/msal-angular';

// RxJS Imports
import { Observable, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';

// Service & Model Imports
import { RoleConst } from 'src/app/Models/roles.const';
import { UserStatusConst } from 'src/app/Models/userStatus.const';
import { LoaderService } from 'src/app/Services/LoaderService/loader.service';
import { CurrentUserService } from 'src/app/Services/CurrentUser/current-user.service';
import { UserService } from 'src/app/Services/User/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private msalGuard: MsalGuard,
    private router: Router,
    private userService: UserService,
    private currentUserService: CurrentUserService,
    private loaderService: LoaderService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.msalGuard.canActivate(route, state).pipe(
      switchMap((result) => {
        if (result !== true) {
          return of(result);
        }

        // The user is logged into Azure AD.
        // Now we validate against the database (EWRS logic).
        return this.handleAuthorization(route);
      }),
      catchError((error) => {
        console.error('Critical error in AuthGuard:', error);
        // Extract serializable error details for critical errors too
        const errorDetails = {
          status: error instanceof HttpErrorResponse ? error.status : 0,
          message:
            error instanceof HttpErrorResponse
              ? error.message
              : error?.toString() || 'Critical error',
          url: error instanceof HttpErrorResponse ? error.url : null,
          timestamp: Date.now(),
        };
        this.router.navigate(['/autherror'], { state: { errorDetails } });
        return of(false);
      })
    );
  }

  /**
   * Custom business logic (Roles, Status, etc.)
   * Note: We no longer call acquireTokenSilent manually.
   * The MsalInterceptor will intercept the 'getMe' call and automatically set the token.
   */
  private handleAuthorization(route: ActivatedRouteSnapshot): Observable<boolean> {
    this.loaderService.show();

    return this.userService.getMe().pipe(
      map((data: any) => {
        this.loaderService.hide();

        // Invalid HTTP response validation (although catchError usually catches this)
        if (data instanceof HttpErrorResponse) {
          if (data.status == 401) {
            this.router.navigate(['/session-expired']);
            return false;
          } else if (data.status == 403) {
            this.router.navigate(['/accessdenied']);
            return false;
          }

          throw data;
        }

        // Null data validation
        if (!data?.value) {
          console.error('Data value is null', data);
          this.router.navigate(['/autherror']);
          return false;
        }

        // Cache the user for the rest of the app (components should read from CurrentUserService)
        this.currentUserService.setUser(data.value);

        // --- Role Logic ---
        const roles: any[] = data.value.userRoles;
        const authorizationIdReq =
          route.data && route.data.authorizationIdReq ? route.data.authorizationIdReq : null;

        const isAuthorized = this.isAuthorized(roles, authorizationIdReq);
        const deniedRoute = this.getDeniedRoute(data.value.userStatusId, route);

        if (!isAuthorized && authorizationIdReq != null) {
          this.router.navigate([deniedRoute]);
          return false;
        }

        // Manager-only routes gate on user profile flags rather than roles,
        // mirroring the nav-link visibility in AppComponent.
        const requireManager = route.data?.['requireManager'] === true;
        if (requireManager && data.value.isManager !== true) {
          this.router.navigate([deniedRoute]);
          return false;
        }

        const requireActiveMyTeamMembers = route.data?.['requireActiveMyTeamMembers'] === true;
        if (requireActiveMyTeamMembers && data.value.hasActiveMyTeamMembers !== true) {
          this.router.navigate([deniedRoute]);
          return false;
        }

        // --- User State Logic ---
        switch (data.value.userStatusId) {
          case UserStatusConst.Active:
            return true; // Access granted

          case UserStatusConst.UnRegistered:
            // If you are not registered, you can only see the registration page
            if (route.routeConfig?.path !== 'register') {
              this.router.navigate(['/register']);
              return false;
            }
            return true;

          default:
            this.router.navigate(['/accessdenied']);
            return false;
        }
      }),
      catchError((err) => {
        this.loaderService.hide();
        console.error('Error obtaining user profile:', err);

        if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
          if (err.status == 401) {
            this.router.navigate(['/session-expired']);
          } else if (err.status == 403) {
            this.router.navigate(['/accessdenied']);
          }
        } else {
          // Extract serializable error details for error ID generation
          const errorDetails = {
            status: err instanceof HttpErrorResponse ? err.status : 0,
            message:
              err instanceof HttpErrorResponse ? err.message : err?.toString() || 'Unknown error',
            url: err instanceof HttpErrorResponse ? err.url : null,
            timestamp: Date.now(),
          };
          this.router.navigate(['/autherror'], { state: { errorDetails } });
        }
        return of(false);
      })
    );
  }

  // Registered users go home; the access-denied page is the registration entry point for everyone else.
  // Home itself falls back to access denied so a user who cannot open home does not loop.
  private getDeniedRoute(userStatusId: number, route: ActivatedRouteSnapshot): string {
    return userStatusId === UserStatusConst.Active && route.routeConfig?.path !== 'home'
      ? '/home'
      : '/accessdenied';
  }

  isAuthorized(userRoles: any[], requirement: number): boolean {
    if (userRoles == null) {
      return false;
    }
    for (let r of userRoles) {
      if (r?.roleTypeId === requirement || r?.roleTypeId == RoleConst.SuperUser) {
        return true;
      }
    }
    return false;
  }
}
